import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DataTable,
  Pagination,
  StatusBadge,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { HiArrowLeft, HiArrowDownTray } from 'react-icons/hi2';
import { useReunificationEvents } from '../../hooks/queries/useReunification';
import { exportEventData } from '../../services/documents.service';
import {
  accountedFromStats,
  unaccountedFromStats,
  type ReunificationStatus,
} from '../../types/ReunificationTypes';
import { toaster } from '../ui/toaster';

interface IncidentReportsProps {
  onBack: () => void;
}

interface EventRow {
  id: string;
  school: string;
  plan: string;
  incident_type: string;
  is_drill: boolean;
  status: string;
  completed_at: string;
  total_students: number;
  reunified: number;
}

const statusColorMap: Record<ReunificationStatus, StatusType> = {
  pending: 'warning',
  active: 'info',
  paused: 'warning',
  completed: 'success',
  cancelled: 'error',
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export function IncidentReports({ onBack }: IncidentReportsProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const pageSize = 20;

  const columns: Column<EventRow>[] = useMemo(
    () => [
      { key: 'school', label: t('nav.sites', 'School'), sortable: true },
      { key: 'plan', label: t('reunification.plan', 'Plan'), sortable: true },
      { key: 'incident_type', label: t('events.incident', 'Incident'), sortable: true },
      {
        key: 'is_drill',
        label: t('events.toggle.drills', 'Drill'),
        render: (row) => (row.is_drill ? t('common.yes', 'Yes') : '—'),
      },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        render: (row) => (
          <StatusBadge
            label={row.status}
            status={statusColorMap[row.status as ReunificationStatus] ?? 'info'}
          />
        ),
      },
      {
        key: 'completed_at',
        label: t('events.completed', 'Completed'),
        sortable: true,
        render: (row) =>
          row.completed_at
            ? new Date(row.completed_at).toLocaleDateString()
            : '—',
      },
      { key: 'total_students', label: t('reunification.students', 'Students'), sortable: true },
      { key: 'reunified', label: t('reunification.reunified', 'Reunified'), sortable: true },
    ],
    [t],
  );

  const { data, isLoading, error } = useReunificationEvents({
    status: 'completed',
    page,
    page_size: pageSize,
    ordering: '-completed_at',
  });

  const handleExport = async (
    eventId: string,
    format: 'csv' | 'xlsx' | 'json',
  ) => {
    setExporting(eventId);
    try {
      const blob = await exportEventData(eventId, {
        format,
        include_students: true,
        include_guardians: true,
        include_staff: true,
        include_audit_log: true,
      });
      const ext = format === 'xlsx' ? 'xlsx' : format;
      downloadBlob(blob, `incident-report-${eventId}.${ext}`);
      toaster.create({
        title: t('documents.exportCompleteTitle', 'Export complete'),
        description: t('documents.exportCompleteDesc', 'Report downloaded as {{format}}.', { format: format.toUpperCase() }),
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      toaster.create({
        title: t('documents.exportFailedTitle', 'Export failed'),
        description:
          err instanceof Error ? err.message : t('documents.exportFailedDesc', 'Failed to export report.'),
        type: 'error',
        duration: 5000,
      });
    } finally {
      setExporting(null);
    }
  };

  const rows: EventRow[] =
    data?.results?.map((e) => ({
      id: e.public_id,
      school: e.school_name,
      plan: e.plan_name ?? '—',
      incident_type: e.incident_type_display,
      is_drill: e.is_drill,
      status: e.status_display,
      completed_at: e.completed_at ?? '',
      total_students: e.stats.total_students,
      reunified: e.stats.students_reunified,
    })) ?? [];

  const selectedEvent = selectedId
    ? data?.results?.find((e) => e.public_id === selectedId)
    : null;

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex align="center" gap={3} mb={6}>
        <ButtonComponent
          visual="secondary"
          size="sm"
          onClick={selectedId ? () => setSelectedId(null) : onBack}
         data-e2e="back-button">
          <HiArrowLeft />
          {t('common.back', 'Back')}
        </ButtonComponent>
        <Box>
          <Heading as="h2" fontSize="lg" fontWeight={600} color="text.primary">
            {t('documents.incidentReports', 'Incident Reports')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {selectedId
              ? t('documents.exportAfterActionReport', 'Export after-action report data')
              : t('documents.selectEventToExport', 'Select an event to export its report')}
          </Text>
        </Box>
      </Flex>

      {/* Export panel for selected event */}
      {selectedEvent && (
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.primary"
          borderRadius="lg"
          p={5}
          mb={6}
        >
          <Text fontWeight={600} color="text.primary" mb={1}>
            {selectedEvent.school_name} &mdash;{' '}
            {selectedEvent.incident_type_display}
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={4}>
            {selectedEvent.plan_name ?? t('reunification.noPlan', 'No plan')} &bull;{' '}
            {selectedEvent.completed_at
              ? new Date(selectedEvent.completed_at).toLocaleDateString()
              : t('events.inProgress', 'In progress')}
            {selectedEvent.is_drill && ` (${t('events.toggle.drills', 'Drill')})`}
          </Text>

          <SimpleGrid columns={{ base: 2, md: 4 }} gap={3} mb={4}>
            <Box textAlign="center">
              <Text fontSize="2xl" fontWeight={700} color="text.primary">
                {selectedEvent.stats.total_students}
              </Text>
              <Text fontSize="xs" color="text.secondary">
                {t('reunification.students', 'Students')}
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="2xl" fontWeight={700} color="text.primary">
                {selectedEvent.stats.students_reunified}
              </Text>
              <Text fontSize="xs" color="text.secondary">
                {t('reunification.reunified', 'Reunified')}
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="2xl" fontWeight={700} color="text.primary">
                {accountedFromStats(selectedEvent.stats)}
              </Text>
              <Text fontSize="xs" color="text.secondary">
                {t('reunification.accounted', 'Accounted')}
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="2xl" fontWeight={700} color="text.primary">
                {unaccountedFromStats(selectedEvent.stats)}
              </Text>
              <Text fontSize="xs" color="text.secondary">
                {t('reunification.unaccounted', 'Unaccounted')}
              </Text>
            </Box>
          </SimpleGrid>

          <Text fontSize="sm" fontWeight={600} color="text.secondary" mb={2}>
            {t('documents.exportFormat', 'Export Format')}
          </Text>
          <Flex gap={3}>
            <ButtonComponent
              visual="primary"
              size="sm"
              onClick={() => handleExport(selectedEvent.public_id, 'xlsx')}
              disabled={exporting === selectedEvent.public_id}
             data-e2e="excel-xlsx-button">
              <HiArrowDownTray />
              Excel (.xlsx)
            </ButtonComponent>
            <ButtonComponent
              visual="secondary"
              size="sm"
              onClick={() => handleExport(selectedEvent.public_id, 'csv')}
              disabled={exporting === selectedEvent.public_id}
             data-e2e="csv-button">
              <HiArrowDownTray />
              CSV
            </ButtonComponent>
            <ButtonComponent
              visual="secondary"
              size="sm"
              onClick={() => handleExport(selectedEvent.public_id, 'json')}
              disabled={exporting === selectedEvent.public_id}
             data-e2e="json-button">
              <HiArrowDownTray />
              JSON
            </ButtonComponent>
          </Flex>
        </Box>
      )}

      {isLoading && (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      )}

      {error && (
        <Center py={12}>
          <Text color="text.danger">
            {error instanceof Error
              ? error.message
              : t('documents.failedLoadEvents', 'Failed to load events')}
          </Text>
        </Center>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Center py={12}>
          <Text color="text.secondary">
            {t('documents.noCompletedEventsFound', 'No completed reunification events found.')}
          </Text>
        </Center>
      )}

      {rows.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(row) => setSelectedId(row.id)}
            keyExtractor={(row) => row.id}
          />
          {data && data.count > pageSize && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(data.count / pageSize)}
              onPageChange={setPage}
              pageSize={pageSize}
            />
          )}
        </>
      )}
    </Box>
  );
}
